import { useQuery } from '@tanstack/react-query';
import { Column } from 'primereact/column';
import {
  DataTable,
  DataTableExpandedRows,
  DataTableRowClickEvent,
  DataTableRowToggleEvent,
  DataTableStateEvent,
  DataTableValueArray,
} from 'primereact/datatable';
import { Skeleton } from 'primereact/skeleton';
import { classNames } from 'primereact/utils';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import PushedCertificateIcon from '../../../../../../../../assets/icons/PushedCertificateIcon';
import RequestedCertificateIcon from '../../../../../../../../assets/icons/RequestedCertificateIcon';
import EmptyScreen from '../../../../../../../../components/empty-screen/EmptyScreen';
import TooltipIcon from '../../../../../../../../components/tooltip-icon/TooltipIcon';
import { useGetPaginatedSupplierCertificates } from '../../../../../../../../hooks/api/certificates';
import {
  DEFAULT_SORT,
  useSupplierCertificatesTableQueryParams,
} from '../../../../../../../../hooks/useSupplierCertificatesTableQueryParams';
import { Locale } from '../../../../../../../../i18n';
import { getPaginatorTemplate } from '../../../../../../../../shared/helpers/get-paginator-template';
import {
  OPTIMISTIC_SUPPLIER_CERTIFICATES_QUERY_KEY,
  OptimisticSupplierCertificate,
} from '../../../../../../../../shared/helpers/optimistic-supplier-certificates';
import { parseDateWithoutTime } from '../../../../../../../../shared/helpers/parse-date';
import {
  CertificatePermissionReviewStatus,
  CertificateSource,
  CertificateStatus,
  ISupplierCertificatesTableRow,
  RequestReviewStatus,
} from '../../../../../../../../shared/interfaces/ICertificate';
import SupplierCertificateReviewButton from '../../../../../supplier-certificate-review-button/SupplierCertificateReviewButton';
import SupplierCertificateReviewStatusTag from '../../../../../supplier-certificate-review-status-tag/SupplierCertificateReviewStatusTag';
import RequestCertificateButton from '../request-certificate-button/RequestCertificateButton';
import ReviewStatusFilter from '../review-status-filter/ReviewStatusFilter';
import styles from './SupplierCertificatesTable.module.css';

// Row id can be null and rejected request rows can otherwise be identical, so we
// attach a stable unique key per row for the DataTable to track row expansion by.
type SupplierCertificateRow = Omit<ISupplierCertificatesTableRow, 'id'> & {
  // Optimistic (not-yet-persisted) rows have no id, so the table treats them as non-clickable.
  id?: number;
  rowKey: string;
  isOptimistic?: boolean;
};

const SupplierCertificatesTable = (): JSX.Element => {
  const { skip, size, sort, filters, setParams } = useSupplierCertificatesTableQueryParams();
  const [sortField, sortOrder] = sort ? sort.split('-') : [undefined, undefined];
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [expandedRows, setExpandedRows] = useState<
    DataTableExpandedRows | DataTableValueArray | undefined
  >(undefined);

  const {
    i18n: { language },
    t,
  } = useTranslation();
  const navigate = useNavigate();

  const { data, isLoading } = useGetPaginatedSupplierCertificates({
    ...filters,
    skip,
    size,
    sort,
  });

  useEffect(() => {
    if (data) {
      setTotalRecords(data?.pagination?.total || 0);
    }
    // Reset expansion whenever the underlying page/data changes so stale keys
    // don't leak expansion state onto unrelated rows.
    setExpandedRows(undefined);
  }, [data]);

  // Optimistically-pulled certificates live in a dedicated cache entry (see
  // optimistic-supplier-certificates.ts) so they never collide with the paginated list.
  const { data: optimisticCertificates = [] } = useQuery({
    queryKey: OPTIMISTIC_SUPPLIER_CERTIFICATES_QUERY_KEY,
    queryFn: (): OptimisticSupplierCertificate[] => {
      return [];
    },
    staleTime: Infinity,
    cacheTime: Infinity,
  });

  // We can only place an optimistic row honestly on the default view: first page, default
  // received-desc sort and no active filters. Anywhere else we wait for the refetch to surface
  // the real (correctly sorted/filtered) row.
  const canShowOptimisticRows =
    skip === 0 &&
    sort === DEFAULT_SORT &&
    !Object.values(filters).some((value) => {
      return Boolean(value);
    });

  const tableRows = useMemo<SupplierCertificateRow[]>(() => {
    const realRows = (data?.data ?? []).map((row, index) => {
      return { ...row, rowKey: `${index}` };
    });

    if (!canShowOptimisticRows) {
      return realRows;
    }

    const optimisticRows = optimisticCertificates.map((row) => {
      return { ...row, rowKey: `optimistic-${row.optimisticKey}`, isOptimistic: true };
    }) as SupplierCertificateRow[];

    return [...optimisticRows, ...realRows];
  }, [data, canShowOptimisticRows, optimisticCertificates]);

  // Optimistic rows are still being pulled, so their not-yet-known columns show a skeleton.
  const optimisticCellSkeleton = <Skeleton height='1rem' width='80%' />;

  const validUntilTemplate = (rowData: SupplierCertificateRow): React.ReactNode => {
    if (rowData.isOptimistic) {
      return optimisticCellSkeleton;
    }

    if (!rowData.received) {
      return null;
    }

    if (!rowData.validUntil) {
      return (
        <span className='font-italic text-gray-700'>{t('addCertificateDialog.noExpiration')}</span>
      );
    }

    return parseDateWithoutTime(rowData.validUntil, language as Locale);
  };

  const receivedTemplate = (rowData: SupplierCertificateRow): React.ReactNode => {
    if (rowData.isOptimistic) {
      return optimisticCellSkeleton;
    }

    if (!rowData.received) {
      return null;
    }

    return parseDateWithoutTime(rowData.received, language as Locale);
  };

  const certificateTypeTemplate = (rowData: SupplierCertificateRow): React.ReactNode => {
    return rowData.type;
  };

  const handleRowClick = (event: DataTableRowClickEvent) => {
    const target = event.originalEvent.target as HTMLElement | null;
    const rowData = event.data as SupplierCertificateRow;

    if (!rowData.id) {
      return;
    }

    if (target?.closest('button, a, [role="button"]')) {
      return;
    }

    navigate(`/certificates/supplier-certificates/${rowData.id}`);
  };

  const reviewTemplate = (rowData: SupplierCertificateRow): React.ReactNode => {
    if (rowData.isOptimistic) {
      return optimisticCellSkeleton;
    }

    return <SupplierCertificateReviewStatusTag status={rowData.consumerReviewStatus} />;
  };

  const isRowExpandable = (rowData: SupplierCertificateRow): boolean => {
    return (
      rowData.source === CertificateSource.REQUESTED &&
      rowData.consumerReviewStatus === CertificatePermissionReviewStatus.REJECTED &&
      Boolean(rowData.errors?.length)
    );
  };

  const rowExpansionTemplate = (rowData: SupplierCertificateRow): React.ReactNode => {
    return (
      <div className={styles['error-expansion']}>
        {rowData.errors?.map((error, index) => {
          return (
            <div key={index} className={classNames(styles['error-message'], 'align-items-center')}>
              <i className='pi pi-exclamation-circle' />
              <span>{error.message}</span>
            </div>
          );
        })}
      </div>
    );
  };

  const sourceTemplate = (rowData: SupplierCertificateRow): React.ReactNode => {
    if (!rowData.source) {
      return null;
    }

    const isPushed = rowData.source === CertificateSource.PUSHED;
    const icon = isPushed ? <PushedCertificateIcon /> : <RequestedCertificateIcon />;
    const tooltipText = isPushed
      ? t('certificatesPage.tabs.supplierCertificatesTab.sourceTooltips.pushed')
      : t('certificatesPage.tabs.supplierCertificatesTab.sourceTooltips.requested');

    return (
      <div className='flex w-full justify-content-center'>
        <TooltipIcon
          id={`supplier-source-${rowData.rowKey}`}
          icon={icon}
          tooltipText={tooltipText}
          position='top'
          width='fit'
        />
      </div>
    );
  };

  const supplierTemplate = (rowData: SupplierCertificateRow): React.ReactNode => {
    return (
      <div className='flex flex-column gap-1'>
        <span>{rowData?.supplierName ?? 'Unknown'}</span>
        <span className='text-600 text-xs'>{rowData?.supplierBpn ?? 'Unknown'}</span>
      </div>
    );
  };

  const reviewedByTemplate = (rowData: SupplierCertificateRow): React.ReactNode => {
    if (rowData.isOptimistic) {
      return optimisticCellSkeleton;
    }

    if (rowData.reviewedBy) {
      const firstName = rowData.reviewedBy.firstName || '';
      const lastName = rowData.reviewedBy.lastName || '';
      const fullName = (firstName + ' ' + lastName).trim();

      return fullName || null;
    }

    const canReview =
      rowData?.consumerReviewStatus !== CertificatePermissionReviewStatus.ACCEPTED &&
      rowData?.consumerReviewStatus !== CertificatePermissionReviewStatus.REJECTED &&
      rowData?.consumerReviewStatus !== RequestReviewStatus.REQUESTED &&
      rowData?.status !== CertificateStatus.EXPIRED;

    if (!canReview) {
      return null;
    }

    return (
      <SupplierCertificateReviewButton
        certificateId={rowData.id?.toString() ?? ''}
        className='w-fit py-2 px-3 m-0'
      />
    );
  };

  const hasActiveFilters = Object.values(filters).some((value) => {
    return Boolean(value);
  });

  const isTableEmpty = !isLoading && data?.data?.length === 0 && !hasActiveFilters;

  if (isTableEmpty) {
    return (
      <EmptyScreen>
        <div className='flex flex-column align-items-center p-4 gap-3'>
          <span>{t('certificatesPage.tabs.supplierCertificatesTab.emptyScreen.text')}</span>
          <RequestCertificateButton />
        </div>
      </EmptyScreen>
    );
  }

  return (
    <div className={styles['supplier-certificates-table']}>
      <DataTable
        value={tableRows}
        dataKey='rowKey'
        loading={isLoading}
        header={
          <div className='flex flex-column md:flex-row gap-2 w-full md:align-items-center md:justify-content-between'>
            <RequestCertificateButton />
            <div className='w-full md:w-auto'>
              <ReviewStatusFilter
                filters={filters}
                onReviewStatusFilterChange={(reviewStatuses) => {
                  setParams({ reviewStatus: reviewStatuses, page: 1 });
                }}
              />
            </div>
          </div>
        }
        lazy
        rows={size}
        paginatorPosition='top'
        paginator
        sortField={sortField}
        sortOrder={sortField ? (sortOrder === 'desc' ? -1 : 1) : undefined}
        rowClassName={(rowData: SupplierCertificateRow) => {
          return rowData.id ? 'cursor-pointer supplier-hoverable-row' : '';
        }}
        expandedRows={expandedRows}
        onRowToggle={(event: DataTableRowToggleEvent) => {
          setExpandedRows(event.data);
        }}
        rowExpansionTemplate={rowExpansionTemplate}
        onRowClick={handleRowClick}
        onSort={(event: DataTableStateEvent) => {
          const newSortOrder = sortOrder === 'desc' ? 'asc' : 'desc';
          setParams({
            sort: `${event.sortField}-${newSortOrder}`,
            page: 1,
          });
        }}
        scrollable
        paginatorTemplate={getPaginatorTemplate({
          totalRecords,
          rowsPerPage: size,
          skip,
          onNext: () => {
            setParams({ page: (skip + size) / size + 1 });
          },
          onPrev: () => {
            setParams({ page: (skip + size) / size - 1 });
          },
        })}
        tableStyle={{
          tableLayout: 'fixed',
          minWidth: '1150px',
        }}
        emptyMessage={t('global.emptyTableMessage')}
      >
        <Column expander={isRowExpandable} style={{ width: '3rem' }} />

        <Column
          header={t(
            'certificatesPage.tabs.supplierCertificatesTab.supplierCertificatesTableColumns.source',
          )}
          body={sourceTemplate}
          sortable
          sortField='source'
          style={{ width: '10%' }}
        />

        <Column
          header={t(
            'certificatesPage.tabs.supplierCertificatesTab.supplierCertificatesTableColumns.supplier',
          )}
          body={supplierTemplate}
          sortable
          sortField='supplierName'
          style={{ width: '25%', wordBreak: 'break-word' }}
        />

        <Column
          header={t(
            'certificatesPage.tabs.supplierCertificatesTab.supplierCertificatesTableColumns.certificateType',
          )}
          body={certificateTypeTemplate}
          sortable
          sortField='type'
          style={{ width: '25%', wordBreak: 'break-word' }}
        />

        <Column
          header={t(
            'certificatesPage.tabs.supplierCertificatesTab.supplierCertificatesTableColumns.validUntil',
          )}
          body={validUntilTemplate}
          sortable
          sortField='validUntil'
          style={{ width: '14%' }}
        />

        <Column
          header={t(
            'certificatesPage.tabs.supplierCertificatesTab.supplierCertificatesTableColumns.received',
          )}
          body={receivedTemplate}
          sortable
          sortField='received'
          style={{ width: '14%' }}
        />

        <Column
          header={t(
            'certificatesPage.tabs.supplierCertificatesTab.supplierCertificatesTableColumns.review',
          )}
          body={reviewTemplate}
          style={{ width: '12%' }}
        />

        <Column
          header={t(
            'certificatesPage.tabs.supplierCertificatesTab.supplierCertificatesTableColumns.reviewedBy',
          )}
          body={reviewedByTemplate}
          sortable
          sortField='reviewedBy'
          style={{ width: '16%' }}
        />
      </DataTable>
    </div>
  );
};

export default SupplierCertificatesTable;
